import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Header from './components/layout/Header';
import { lightTheme, darkTheme } from './components/misc/theme';
import { ThemeProvider } from '@emotion/react';
import stockTickers from './data/companylist.json';
import Footer from './components/layout/Footer';
import { cleanLocalStorageOlderThan } from './components/CleanLocalStorage';
import { fetchFavorites, fetchColumnPresets } from './features/favorites/favoritesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import { getDevFlag } from './constants/devFlags';
import { getSoftwareUnitByTicker, isAcquiredSubsidiaryByTicker } from './utils/companyHelpers';
import { applyBrowserChromeTheme } from './utils/browserChromeTheme';
import {
  applySoftwareIqTheme,
  persistSoftwareIqTheme,
  readSoftwareIqThemePreference,
  resolveSoftwareIqTheme,
  SOFTWAREIQ_THEME_STORAGE_KEY,
} from '@softwareiq/brand-ui';
import ExternalRedirect from './components/routing/ExternalRedirect';
import {
  marketingBaseUrl,
  marketingContactUrl,
  marketingPricingUrl,
} from './config/siteUrls';
import { AUTH0_AUDIENCE } from './constants/api';
import {
  getOperatingViewFromGuidanceView,
} from './pages/industry-metrics/subpages/operatingMetricsNavigation';
import { HIRING_VIEWS } from './features/hiring/hiringNavigation';
import AnalyticsProvider from './components/analytics/AnalyticsProvider';

// Lazy-loaded page components for code splitting
const GrowthCurve = lazy(() => import('./pages/preview/subpages/GrowthCurve'));
const PreviewValuation = lazy(() => import('./pages/preview/subpages/Valuation'));
const Success = lazy(() => import('./pages/auth/Success'));
// CoverageUniverse now loaded within MarketInsights
const IndustryMetrics = lazy(() => import('./pages/industry-metrics/IndustryMetrics'));
const DeveloperWorkspacePage = lazy(() => import('./pages/industry-metrics/subpages/Developer'));
const MarketInsights = lazy(() => import('./pages/market-insights/MarketInsights'));
const SecurityAnalysis = lazy(() => import('./pages/security-analysis/SecurityAnalysis'));
const Callback = lazy(() => import('./pages/auth/Callback'));
const Start = lazy(() => import('./pages/auth/Start'));
const SubscribeError = lazy(() => import('./pages/auth/SubscribeError'));
const SourceCheck = lazy(() => import('./pages/source-check/SourceCheck'));
// News subpages now loaded directly in Preview
const IndustryNews = lazy(() => import('./pages/preview/subpages/IndustryNews'));
const Academy = lazy(() => import('./pages/academy/Academy'));
const Mcp = lazy(() => import('./pages/mcp/Mcp'));
const NativeDocumentation = lazy(() => import('./pages/documentation/NativeDocumentation'));
const NativeCoverage = lazy(() => import('./pages/documentation/NativeCoverage'));
const Preview = lazy(() => import('./pages/preview/Preview'));
const PreviewNewsletter = lazy(() => import('./pages/preview/subpages/PreviewNewsletter'));
const PreviewCompanyNewsletter = lazy(() => import('./pages/preview/subpages/PreviewCompanyNewsletter'));
const PreviewEvents = lazy(() => import('./pages/preview/subpages/PreviewEvents'));
const PreviewCompanyEvents = lazy(() => import('./pages/preview/subpages/PreviewCompanyEvents'));
const HiringWorkspacePage = lazy(() => import('./features/hiring/HiringWorkspacePage'));
const FilingChangesWorkspacePage = lazy(() => import('./features/filing-changes/FilingChangesWorkspacePage'));
const FederalDealsWorkspacePage = lazy(() => import('./features/federal-deals/FederalDealsWorkspacePage'));
const SHOW_SI_HIRING = getDevFlag('SI_HIRING');
const SHOW_GUIDANCE = getDevFlag('GUIDANCE_PREVIEW');
const SHOW_FILING_CHANGES = getDevFlag('FILING_INTELLIGENCE_PREVIEW');
const SHOW_FEDERAL_DEALS = getDevFlag('FEDERAL_DEALS');
// Dev-only: product pages read the dev data server's product index.
const SHOW_PRODUCTS = getDevFlag('PRODUCTS');
const ProductPage = lazy(() => import('./pages/product/ProductPage'));

// Lazy-loaded subpage components
// MetricsTrended now loaded within MarketInsights
// Valuation, Segmentation, and Cloud now loaded within MarketInsights
const Private = lazy(() => import('./pages/preview/subpages/Private'));
// Index now loaded within MarketInsights

// Loading fallback component
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

const AppFooter = ({ toggleTheme }) => {
  return <Footer toggleTheme={toggleTheme} />;
};

export const AppHeader = ({ themeMode, toggleTheme }) => {
  return <Header themeMode={themeMode} toggleTheme={toggleTheme} />;
};

const getCompanyDefaultPath = (ticker) => {
  const lower = ticker.toLowerCase();
  if (getSoftwareUnitByTicker(ticker)) return `/company/${lower}/jobpostings`;
  return `/company/${lower}/${isAcquiredSubsidiaryByTicker(ticker) ? 'acquisition' : 'metrics'}`;
};

const LegacyGuidanceRedirect = () => {
  const { depth = 'vendor', view = 'results' } = useParams();
  const { search } = useLocation();
  return (
    <Navigate
      to={{ pathname: `/kpis/${depth}/${getOperatingViewFromGuidanceView(view)}`, search }}
      replace
    />
  );
};

const LegacyMarketBenchmarksRedirect = ({ view: forcedView } = {}) => {
  const { view: routeView } = useParams();
  const { search } = useLocation();
  const requestedView = forcedView || routeView || 'index';
  const view = ['index', 'valuation', 'treemap'].includes(requestedView)
    ? requestedView
    : 'index';
  return <Navigate to={{ pathname: `/benchmarks/${view}`, search }} replace />;
};

const LegacyPartnerTrendsRedirect = () => {
  const { section = 'vendor' } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const targetSection = section === 'market' ? 'category' : section;
  if (section === 'market') searchParams.set('variant', 'total');
  const targetView = HIRING_VIEWS.PARTNER_TRENDS;
  const targetSearch = searchParams.toString();
  return (
    <Navigate
      to={{ pathname: `/hiring/${targetSection}/${targetView}`, search: targetSearch ? `?${targetSearch}` : '' }}
      replace
    />
  );
};

const LegacyDeveloperRedirect = () => {
  const { layer = 'project', view = 'trends' } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const targetSearch = searchParams.toString();
  return (
    <Navigate
      to={{
        pathname: `/adoption/${layer}/${view}`,
        search: targetSearch ? `?${targetSearch}` : '',
      }}
      replace
    />
  );
};

const LegacyCompanyDeveloperRedirect = () => {
  const { ticker } = useParams();
  const { search } = useLocation();
  return (
    <Navigate
      to={{ pathname: `/company/${String(ticker || '').toLowerCase()}/adoption`, search }}
      replace
    />
  );
};

const LegacyAdoptionFamilyRedirect = ({ layer, family }) => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  searchParams.set('family', family);
  const view = layer === 'project-detail' ? 'packages' : 'trends';
  return (
    <Navigate
      to={{ pathname: `/adoption/${layer}/${view}`, search: `?${searchParams.toString()}` }}
      replace
    />
  );
};

const LegacyHiringMarketRedirect = () => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  searchParams.set('variant', 'total');
  return (
    <Navigate
      to={{ pathname: '/hiring/category/partner-trends', search: `?${searchParams.toString()}` }}
      replace
    />
  );
};

function App() {
  // ---- state / hooks (must stay at top) ----
  const [themeMode, setThemeMode] = useState(() => resolveSoftwareIqTheme());

  // Keep the preference synchronized with the marketing application and OS.
  useEffect(() => {
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)');
    const syncTheme = () => {
      setThemeMode(
        readSoftwareIqThemePreference()
          || (systemPreference.matches ? 'dark' : 'light')
      );
    };
    const syncVisibleTheme = () => {
      if (document.visibilityState === 'visible') syncTheme();
    };
    const syncStorageTheme = (event) => {
      if (event.key === SOFTWAREIQ_THEME_STORAGE_KEY) syncTheme();
    };
    const syncSystemTheme = () => {
      if (!readSoftwareIqThemePreference()) syncTheme();
    };

    window.addEventListener('focus', syncTheme);
    window.addEventListener('pageshow', syncTheme);
    window.addEventListener('storage', syncStorageTheme);
    document.addEventListener('visibilitychange', syncVisibleTheme);
    systemPreference.addEventListener('change', syncSystemTheme);
    return () => {
      window.removeEventListener('focus', syncTheme);
      window.removeEventListener('pageshow', syncTheme);
      window.removeEventListener('storage', syncStorageTheme);
      document.removeEventListener('visibilitychange', syncVisibleTheme);
      systemPreference.removeEventListener('change', syncSystemTheme);
    };
  }, []);

  const dispatch = useDispatch();
  const {
    isLoading: authLoading,
    isAuthenticated,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  // gate for “auth is actually usable”
  const [authReady, setAuthReady] = useState(false);

  // redux selector must NOT be after a conditional return
  const favStatus = useSelector((state) => state.favorites.status);

  // ---- Auth bootstrap effect ----
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      // public user → ready to render
      setAuthReady(true);
      return;
    }

    // authenticated → verify token really works
        (async () => {
          try {
            await getAccessTokenSilently({
              audience: AUTH0_AUDIENCE,
            });
            setAuthReady(true);
          } catch (err) {
      console.warn('Token check failed', err);

      const msg = typeof err?.message === 'string' ? err.message : '';
      const code = err?.error;

      const isAuthExpired =
        code === 'login_required' ||
        code === 'consent_required' ||
        msg.includes('Missing Refresh Token');

      if (isAuthExpired) {
        logout({ logoutParams: { returnTo: window.location.origin } });
      } else {
        // allow app to render as unauth
        setAuthReady(true);
      }
    }})();
  }, [authLoading, isAuthenticated, getAccessTokenSilently, logout]);

  // redux selector for column presets status
  const columnPresetsStatus = useSelector((state) => state.favorites.columnPresetsStatus);

  // ---- fetch favorites and column presets only when auth is confirmed ----
  useEffect(() => {
    if (!authReady) return;
    if (isAuthenticated && favStatus === 'idle') {
      (async () => {
        try {
          const token = await getAccessTokenSilently({
            audience: AUTH0_AUDIENCE,
          });
          dispatch(fetchFavorites({ token }));
        } catch (err) {
          console.error('Could not fetch favorites:', err);
        }
      })();
    }
  }, [authReady, isAuthenticated, favStatus, dispatch, getAccessTokenSilently]);

  // ---- fetch column presets when auth is confirmed ----
  useEffect(() => {
    if (!authReady) return;
    if (isAuthenticated && columnPresetsStatus === 'idle') {
      (async () => {
        try {
          const token = await getAccessTokenSilently({
            audience: AUTH0_AUDIENCE,
          });
          dispatch(fetchColumnPresets({ token }));
        } catch (err) {
          console.error('Could not fetch column presets:', err);
        }
      })();
    }
  }, [authReady, isAuthenticated, columnPresetsStatus, dispatch, getAccessTokenSilently]);

  // ---- one-time clean local storage ----
  useEffect(() => {
    cleanLocalStorageOlderThan(1);
  }, []);

  // ---- theme / DOM updates ----
  useEffect(() => {
    applySoftwareIqTheme(themeMode);
    document.body.classList.toggle('dark', themeMode === 'dark');
    document.body.classList.toggle('light', themeMode === 'light');
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
    document.documentElement.classList.toggle('light', themeMode === 'light');

    const root = document.documentElement;
    if (themeMode === 'dark') {
      root.style.setProperty('--scrollbar-track-color', '#333');
      root.style.setProperty('--scrollbar-thumb-color', 'rgb(60,60,73)');
    } else {
      root.style.setProperty('--scrollbar-track-color', 'blue');
      root.style.setProperty('--scrollbar-thumb-color', 'rgb(200,200,229)');
    }

    const activeTheme = themeMode === 'dark' ? darkTheme : lightTheme;
    return applyBrowserChromeTheme({ themeMode, activeTheme });
  }, [themeMode]);

  // ---- session-expired event → force logout ----
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    };
    window.addEventListener('auth:session-expired', handleSessionExpiredEvent);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpiredEvent);
    };
  }, [logout]);

  // ---- axios interceptor: catch 401s from API Gateway ----
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const toggleTheme = () => {
    setThemeMode((prevMode) => {
      const nextMode = prevMode === 'light' ? 'dark' : 'light';
      persistSoftwareIqTheme(nextMode);
      return nextMode;
    });
  };

  return (
    <ThemeProvider theme={themeMode === 'dark' ? darkTheme : lightTheme}>
      <>
        {!authReady ? (
          // loading state while we verify token / session
          <div style={{ padding: 24 }} />
        ) : (
          <Router>
            <AnalyticsProvider />
            <AppHeader themeMode={themeMode} toggleTheme={toggleTheme} />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path='/' element={<Navigate to='/kpis/category/trends' replace />} />

              <Route path='/callback' element={<Callback />} />
              <Route path='/start/:intent' element={<Start />} />
              <Route path='/subscribe-error' element={<SubscribeError />} />
              <Route path='/pricing' element={<ExternalRedirect to={marketingPricingUrl} />} />
              <Route path='/coverage' element={<NativeCoverage />} />
              <Route path='/success' element={<Success />} />
              <Route path='/source-check/:sourceCheckId' element={<SourceCheck />} />
              <Route path='/hiring' element={<Navigate to='/hiring/vendor/trend' replace />} />
              <Route path='/hiring/market/partner-trends' element={<LegacyHiringMarketRedirect />} />
              <Route path='/hiring/:depth/:view' element={<HiringWorkspacePage />} />
              {SHOW_GUIDANCE ? (
                <>
                  <Route path='/guidance' element={<Navigate to='/kpis/vendor/guidance-results' replace />} />
                  <Route path='/guidance/:depth/:view' element={<LegacyGuidanceRedirect />} />
                </>
              ) : (
                <>
                  <Route path='/guidance' element={<Navigate to='/kpis/vendor/table' replace />} />
                  <Route path='/guidance/:depth/:view' element={<Navigate to='/kpis/vendor/table' replace />} />
                </>
              )}
              {SHOW_SI_HIRING && <Route path='/sihiring' element={<Navigate to='/hiring/category/partner-trends?variant=total' replace />} />}
              {SHOW_SI_HIRING && <Route path='/sihiring/:section/:view' element={<LegacyPartnerTrendsRedirect />} />}

              {/* Dataset workspaces */}
              {SHOW_FILING_CHANGES ? (
                <>
                  <Route path='/filingchanges' element={<Navigate to='/filingchanges/vendor/events' replace />} />
                  <Route path='/filingchanges/:depth/:view' element={<FilingChangesWorkspacePage />} />
                </>
              ) : (
                <>
                  <Route path='/filingchanges' element={<Navigate to='/kpis/vendor/table' replace />} />
                  <Route path='/filingchanges/:depth/:view' element={<Navigate to='/kpis/vendor/table' replace />} />
                </>
              )}
              {SHOW_FEDERAL_DEALS && (
                <>
                  <Route path='/federal-deals' element={<Navigate to='/federal-deals/portfolio/deals' replace />} />
                  <Route path='/federal-deals/:section/:view' element={<FederalDealsWorkspacePage />} />
                </>
              )}
              {SHOW_PRODUCTS && <Route path='/product/:productKey' element={<ProductPage />} />}
              {SHOW_PRODUCTS && <Route path='/product/:productKey/:view' element={<ProductPage />} />}
              <Route path='/kpis' element={<Navigate to='/kpis/category/trends' replace />} />
              <Route path='/kpis/:section/:view' element={<IndustryMetrics />} />
              <Route path='/adoption' element={<Navigate to='/adoption/project/trends' replace />} />
              <Route path='/adoption/category/packages' element={<LegacyAdoptionFamilyRedirect layer="category" family="packages" />} />
              <Route path='/adoption/category/infrastructure' element={<LegacyAdoptionFamilyRedirect layer="category" family="infrastructure" />} />
              <Route path='/adoption/project/packages' element={<LegacyAdoptionFamilyRedirect layer="project" family="packages" />} />
              <Route path='/adoption/project/infrastructure' element={<LegacyAdoptionFamilyRedirect layer="project" family="infrastructure" />} />
              <Route path='/adoption/project-detail/infrastructure' element={<LegacyAdoptionFamilyRedirect layer="project-detail" family="infrastructure" />} />
              <Route path='/adoption/:layer/:view' element={<DeveloperWorkspacePage />} />
              <Route path='/developer' element={<Navigate to='/adoption/project/trends' replace />} />
              <Route path='/developer/company/:view' element={<LegacyDeveloperRedirect />} />
              <Route path='/developer/:layer/:view' element={<LegacyDeveloperRedirect />} />
              <Route path='/benchmarks' element={<Navigate to='/benchmarks/index' replace />} />
              <Route path='/benchmarks/:view' element={<MarketInsights />} />
              <Route path='/techstack' element={<IndustryMetrics />} />
              <Route path='/about' element={<ExternalRedirect to={`${marketingBaseUrl}/platform`} />} />
              <Route path='/contact' element={<ExternalRedirect to={marketingContactUrl} />} />

              {/* Market Insights Routes */}
              <Route path='/marketinsights' element={<Navigate to='/marketinsights/coverage' />} />
              <Route path='/marketinsights/index' element={<LegacyMarketBenchmarksRedirect />} />
              <Route path='/marketinsights/index/:view' element={<LegacyMarketBenchmarksRedirect />} />
              <Route path='/marketinsights/segmentation' element={<LegacyMarketBenchmarksRedirect view='treemap' />} />
              <Route path='/marketinsights/coverage/segmentation' element={<LegacyMarketBenchmarksRedirect view='treemap' />} />
              <Route path='/marketinsights/coverage/coverage/segmentation' element={<LegacyMarketBenchmarksRedirect view='treemap' />} />
              <Route path='/marketinsights' element={<MarketInsights />}>
                <Route path='coverage' element={null} />
                <Route path='coverage/:section' element={null} />
                <Route path='coverage/:section/:view' element={null} />
                <Route path='iaas' element={null} />
                <Route path='iaas/:view' element={null} />
                <Route path='acquisitions' element={null} />
                <Route path='venture-backed' element={null} />
                <Route path='earnings' element={null} />
                <Route path='exchangevolume' element={null} />
                <Route path='growthcurve' element={null} />
              </Route>

              <Route path='/mcp' element={getDevFlag('MCP') ? <Mcp /> : <Navigate to='/documentation' replace />} />
              <Route path='/documentation/*' element={<NativeDocumentation />} />

              <Route path='/academy' element={<Navigate to='/academy/metrics' replace />} />
              <Route path='/academy' element={<Academy />}>
                <Route path='metrics'      element={null} />
                <Route path='revrecog'     element={null} />
                <Route path='revlifecycle' element={null} />
              </Route>

              {stockTickers.map((ticker) => (
                <Route
                  key={`navigate-${ticker.toLowerCase()}`}
                  path={`/company/${ticker.toLowerCase()}`}
                  element={<Navigate to={getCompanyDefaultPath(ticker)} />}
                />
              ))}

              {stockTickers.map((ticker) => (
                <Route
                  key={ticker.toLowerCase()}
                  path={`/company/${ticker.toLowerCase()}`}
                  element={<SecurityAnalysis ticker={ticker} />}
                >
                  <Route path='metrics' element={null} />
                  <Route path='metrics/:securityAnalysisCompsView' element={null} />
                  <Route path='keymetrics' element={null} />
                  <Route path='keymetrics/:securityAnalysisView' element={null} />
                  <Route path='guidance' element={null} />
                  <Route path='guidance/:securityAnalysisGuidanceView' element={null} />
                  <Route path='filings' element={null} />
                  <Route path='filings/:securityAnalysisFilingView' element={null} />
                  <Route path='private' element={<Private />} />
                  <Route path='acquisition' element={null} />
                  <Route path='comps' element={null} />
                  <Route path='snapshot-comps' element={null} />
                  <Route path='jobpostings' element={null} />
                  <Route path='jobpostings/:securityAnalysisJobsView' element={null} />
                  <Route path='developer' element={<LegacyCompanyDeveloperRedirect />} />
                  <Route path='adoption' element={null} />
                  <Route path='partnerhiring' element={null} />
                </Route>
              ))}

              {/* Preview Hub - consolidates all preview features */}
              <Route path='/preview' element={<Preview />}>
                <Route index element={<Navigate to='newsletter' replace />} />
                <Route path='newsletter' element={<PreviewNewsletter />} />
                <Route path='newsletter/:ticker' element={<PreviewCompanyNewsletter />} />
                <Route path='events' element={<PreviewEvents />} />
                <Route path='events/:ticker' element={<PreviewCompanyEvents />} />
                <Route path='growth/growth' element={<GrowthCurve />} />
                <Route path='growth/valuation' element={<PreviewValuation />} />
                <Route path='news' element={<IndustryNews />} />
              </Route>
            </Routes>
            </Suspense>
            <AppFooter toggleTheme={toggleTheme} />
          </Router>
        )}
        <ToastContainer />
      </>
    </ThemeProvider>
  );
}

export default App;
